/* eslint-disable react-refresh/only-export-components */
import { Box, SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/system';

// A friendly waving hand for daytime hellos.
const waveAnim = keyframes`
  0%   { transform: rotate(0deg); }
  12%  { transform: rotate(14deg); }
  24%  { transform: rotate(-8deg); }
  36%  { transform: rotate(14deg); }
  48%  { transform: rotate(-4deg); }
  60%  { transform: rotate(10deg); }
  72%  { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
`;

// A gentle bob + head-tilt for the late-night owl.
const owlAnim = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%      { transform: translateY(-4px) rotate(-8deg); }
  50%      { transform: translateY(0) rotate(0deg); }
  75%      { transform: translateY(-4px) rotate(8deg); }
`;

export type GreetingKind = 'morning' | 'afternoon' | 'evening' | 'night';

type GreetingSpec = {
  word: string;
  emoji: string;
  anim: 'wave' | 'owl';
  subtitles: string[];
};

// Casual, cheeky one-liners — the tone you get when you open Claude.
const SPECS: Record<GreetingKind, GreetingSpec> = {
  morning: {
    word: 'Morning',
    emoji: '👋',
    anim: 'wave',
    subtitles: [
      "Let's make today count. ☕",
      'Coffee first, events second.',
      'Rise and grind — the attendees await.',
      'Fresh day, clean slate.',
      "Who's running this show? You are. 🫵🏻",
      'Bright and early. Showoff. 😏',
      'New day, new sign-ups. ✨',
      "Let's fill some seats today.",
      'The early organizer gets the RSVP.',
      "The calendar's ready when you are.",
      'Caffeinated and dangerous. ☕',
      "Today's agenda: run the world.",
    ],
  },
  afternoon: {
    word: 'Afternoon',
    emoji: '👋',
    anim: 'wave',
    subtitles: [
      "Hope the day's behaving.",
      'Powering through, I see. 💪',
      "Let's keep the momentum going.",
      "The events won't manage themselves — you do. 🫵🏻",
      'Halfway there. Keep going.',
      'Right on schedule. Probably.',
      'Snack break earned. 🍪',
      'The inbox can wait. Mostly.',
      'Cruising through the to-do list.',
      'Peak productivity hours. Allegedly.',
      'Keep those tickets moving. 🎟️',
      "You've got this. Obviously.",
    ],
  },
  evening: {
    word: 'Evening',
    emoji: '👋',
    anim: 'wave',
    subtitles: [
      'Winding down, or just getting started?',
      "Let's knock out a few more.",
      "The events won't manage themselves — you do. 🫵🏻",
      'Golden hour, golden numbers.',
      'Still here? Dedication. 🙌',
      'One more thing, then dinner.',
      'The night is young. So are the deadlines.',
      'Wrapping up like a pro. 🎁',
      'Last lap energy. 🏁',
      'Dinner can wait — almost done.',
      'Sunset shift, sunrise results.',
      'Closing strong. 💪',
    ],
  },
  night: {
    word: 'Up late',
    emoji: '🦉',
    anim: 'owl',
    subtitles: [
      'Burning the midnight oil, I see.',
      'The dashboard never sleeps. Apparently neither do you.',
      'Night shift? You and me both.',
      "We don't judge. Much.",
      'Who needs sleep when there are events? 🫵🏻',
      "It's giving… overtime.",
      'The owls are impressed. 🦉',
      'Caffeine is a personality now.',
      'Sleep is for people without events.',
      '3 AM ideas hit different.',
      'Still going? Legend. 🌙',
      'The stars are your coworkers tonight.',
    ],
  },
};

const kindForHour = (hour: number): GreetingKind => {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
};

const firstName = (name?: string) => name?.trim().split(/\s+/)[0] || '';

export type Greeting = {
  kind: GreetingKind;
  heading: string;
  subtitle: string;
  emoji: string;
};

/**
 * Casual, name-aware greeting. Pass the user's name to personalise it.
 *
 * `seed` (0–1) chooses which subtitle to show — pass a value frozen per page load
 * (e.g. `useState(() => Math.random())`) so the message changes on every refresh
 * but stays put while a component re-renders (the navbar's live clock). Without a
 * seed it falls back to a stable date-based pick.
 */
export const getGreeting = (
  date: Date = new Date(),
  name?: string,
  seed?: number
): Greeting => {
  const kind = kindForHour(date.getHours());
  const spec = SPECS[kind];
  const who = firstName(name);
  const heading = who ? `${spec.word}, ${who}` : spec.word;
  const idx =
    seed != null
      ? Math.min(
          spec.subtitles.length - 1,
          Math.floor(seed * spec.subtitles.length)
        )
      : date.getDate() % spec.subtitles.length;
  return { kind, heading, subtitle: spec.subtitles[idx], emoji: spec.emoji };
};

interface AnimatedEmojiProps {
  kind: GreetingKind;
  fontSize?: string | number;
  /** Set false to render the emoji still (e.g. the navbar). Defaults to true. */
  animate?: boolean;
  sx?: SxProps<Theme>;
}

/** The greeting emoji, optionally with its matching CSS animation (reduced-motion safe). */
export const AnimatedEmoji = ({
  kind,
  fontSize = '1.05em',
  animate = true,
  sx,
}: AnimatedEmojiProps) => {
  const spec = SPECS[kind];
  const isOwl = spec.anim === 'owl';
  return (
    <Box
      component='span'
      aria-hidden
      sx={{
        display: 'inline-block',
        lineHeight: 1,
        fontSize,
        ...(animate
          ? {
              transformOrigin: '70% 80%',
              animation: `${isOwl ? owlAnim : waveAnim} ${
                isOwl ? '3.2s' : '2.6s'
              } ease-in-out infinite`,
            }
          : {}),
        ...sx,
      }}
    >
      {spec.emoji}
    </Box>
  );
};
